import { Suspense, useCallback, useEffect, useRef } from 'react';
import { graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';
import { useModelerContext } from '../context/useModelerContext';
import type { SpSiteListsProviderQuery } from './__generated__/SpSiteListsProviderQuery.graphql';

export const SpSiteListsProvider = () => {
    const eventBus = useModelerContext().get('eventBus');
    const [queryRef, loadQuery] = useQueryLoader<SpSiteListsProviderQuery>(query);
    const queueRef = useRef<string[]>([]);
    const processingRef = useRef(false);

    const processNext = useCallback(() => {
        if (queueRef.current.length === 0) {
            processingRef.current = false;
            return;
        }
        const siteUrl = queueRef.current.shift()!;
        processingRef.current = true;
        eventBus.fire('spSiteListsFetched', siteUrl, {
            loading: true,
            data: undefined,
            error: undefined,
        });
        loadQuery({ siteUrl });
    }, [eventBus, loadQuery]);

    useEffect(() => {
        const handler = (_event: any, siteUrl: string) => {
            if (!queueRef.current.includes(siteUrl)) {
                queueRef.current.push(siteUrl);
            }
            if (!processingRef.current) {
                processNext();
            }
        };

        eventBus.on('fetchSpSiteLists', handler);
        return () => eventBus.off('fetchSpSiteLists', handler);
    }, [eventBus, processNext]);

    return (
        queryRef && (
            <Suspense>
                <SiteListsProvider queryRef={queryRef} onDone={processNext} />
            </Suspense>
        )
    );
};

const query = graphql`
    query SpSiteListsProviderQuery($siteUrl: String!) {
        getSharePointLists(siteUrl: $siteUrl) {
            id
            name: title
        }
    }
`;

const SiteListsProvider = (props: {
    queryRef: PreloadedQuery<SpSiteListsProviderQuery>;
    onDone: () => void;
}) => {
    const lists = usePreloadedQuery(query, props.queryRef).getSharePointLists;
    const eventBus = useModelerContext().get('eventBus');

    useEffect(() => {
        if (lists == null) {
            eventBus.fire('spSiteListsFetched', props.queryRef.variables.siteUrl, {
                loading: false,
                data: [],
                error: new Error('Listen konnten nicht geladen werden.'),
            });
        } else {
            eventBus.fire('spSiteListsFetched', props.queryRef.variables.siteUrl, {
                loading: false,
                data: [...lists].sort((a, b) => a.name.localeCompare(b.name)),
                error: undefined,
            });
        }
        props.onDone(); // nächste URL aus Queue verarbeiten
    }, [props.queryRef]);

    return null;
};
